import React, { useEffect, useState } from 'react'
import useI18n from '@/hooks/useI18n'
import { countdownParts, pad2 } from '@/features/brazil-elections/utils'
import MidtermBackdrop from '@/features/home-highlight/MidtermBackdrop'
import '@/features/home-highlight/Shimmer'
import { MIDTERMS_ELECTION_DATE } from '@/constants/midterms'
import UsStateMap from './UsStateMap'
import { PARTY_COLORS } from './midtermRaces'
import { CARD, FAINT, GLASS_FILL, INK, NOTCH, TAB_OFF, TAB_ON, TAB_PILL } from './midtermChrome'

const FIGURES = 'font-[family-name:var(--font-figures)]'
const EMPTY_BY_STATE = new Map()

const TABS = [
  { id: 'all', labelKey: 'midterm_map.tab_all' },
  { id: 'senate', labelKey: 'midterm_map.tab_senate' },
  { id: 'house', labelKey: 'midterm_map.tab_house' },
  { id: 'governor', labelKey: 'midterm_map.tab_governors' },
]

/**
 * A soft highlight over the card surface — Neutral-3 gray on light, a
 * faint white sweep on the dark party wash.
 */
const GlassBlock = ({ className = '' }) => (
  <div className={`relative overflow-hidden ${GLASS_FILL} ${className}`}>
    <div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-Neutral-4/20 to-transparent [html[data-theme='dark']_&]:via-white/12"
      style={{ animation: 'shimmer 2s infinite', transform: 'translateX(-100%)' }}
    />
  </div>
)

const BannerSkeleton = () => (
  <div className={`relative overflow-hidden ${CARD} p-3 md:p-6`}>
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <GlassBlock className="h-12 w-12 shrink-0 rounded-lg border border-Neutral-3 md:h-16 md:w-16" />
        <div className="min-w-0 flex-1">
          <GlassBlock className="h-2.5 w-28 rounded" />
          <GlassBlock className="mt-2 h-5 w-40 rounded md:h-7 md:w-56" />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
        <GlassBlock className="size-10 rounded-full md:size-14" />
        <div className="text-right">
          <GlassBlock className="ml-auto h-7 w-14 rounded md:h-10 md:w-20" />
          <GlassBlock className="mt-1 ml-auto h-3 w-16 rounded" />
        </div>
      </div>
    </div>
    <div className="relative mt-3 h-28 w-full overflow-hidden rounded-lg md:mt-4 md:h-44">
      <svg viewBox="0 0 200 80" className="h-full w-full" aria-hidden="true" preserveAspectRatio="none">
        <path d="M0 42 C 30 38, 50 28, 80 32 S 130 18, 160 22 S 190 14, 200 16" fill="none" stroke={PARTY_COLORS.democratic} strokeWidth="1.5" strokeOpacity="0.45" vectorEffect="non-scaling-stroke" />
        <path d="M0 48 C 30 52, 50 58, 80 54 S 130 64, 160 60 S 190 68, 200 66" fill="none" stroke="var(--color-informative2-4)" strokeWidth="1.5" strokeOpacity="0.45" vectorEffect="non-scaling-stroke" />
      </svg>
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-Neutral-4/15 to-transparent [html[data-theme='dark']_&]:via-white/10"
        style={{ animation: 'shimmer 2s infinite', transform: 'translateX(-100%)' }}
      />
    </div>
    <div className="mt-3 md:mt-4">
      <div className="relative flex h-2.5 w-full gap-1" aria-hidden="true">
        <span className="h-full w-1/2 rounded-full" style={{ background: PARTY_COLORS.democratic, opacity: 0.55 }} />
        <span className="h-full flex-1 rounded-full" style={{ background: PARTY_COLORS.republican, opacity: 0.55 }} />
        <span className={`absolute left-1/2 top-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full ${NOTCH}`} />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <GlassBlock className="h-3 w-24 rounded md:w-28" />
        <GlassBlock className="h-3 w-24 rounded md:w-28" />
      </div>
    </div>
  </div>
)

const RaceCardSkeleton = () => (
  <div className={`${CARD} p-3 shadow-[0_16px_40px_rgba(0,0,0,0.12)]`}>
    <div className="flex items-center gap-3">
      <GlassBlock className="h-10 w-10 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1">
        <GlassBlock className="h-3.5 w-4/5 rounded" />
        <GlassBlock className="mt-2 h-3 w-1/3 rounded" />
      </div>
      <GlassBlock className="h-6 w-12 shrink-0 rounded" />
    </div>
    <div className="mt-3 flex h-1.5 w-full gap-1 px-2" aria-hidden="true">
      <span className="h-full w-[55%] rounded-full" style={{ background: PARTY_COLORS.democratic, opacity: 0.55 }} />
      <span className="h-full flex-1 rounded-full" style={{ background: PARTY_COLORS.republican, opacity: 0.55 }} />
    </div>
    <div className="mt-2 flex items-center justify-between px-2">
      <GlassBlock className="h-2.5 w-20 rounded md:w-24" />
      <GlassBlock className="h-2.5 w-20 rounded md:w-24" />
    </div>
  </div>
)

const SkeletonCountdown = ({ i18n }) => {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const parts = countdownParts(MIDTERMS_ELECTION_DATE, now)
  if (parts.finished) {
    return <span className={`${FIGURES} text-lg font-black uppercase tracking-[0.14em]`}>{i18n('midterm_map.election_day')}</span>
  }

  const units = [
    { value: String(parts.days), label: i18n('midterm_map.countdown_days') },
    { value: pad2(parts.hours), label: i18n('midterm_map.countdown_hours') },
    { value: pad2(parts.minutes), label: i18n('midterm_map.countdown_minutes') },
    { value: pad2(parts.seconds), label: i18n('midterm_map.countdown_seconds') },
  ]

  return (
    <div className="flex items-start gap-2 md:gap-4" role="timer" aria-label={i18n('midterm_map.election_day')}>
      {units.map((unit, index) => (
        <React.Fragment key={unit.label}>
          {index > 0 && <span className={`${FAINT} pt-0.5 text-xl font-black md:text-3xl`}>:</span>}
          <span className="flex flex-col items-center">
            <span className={`${FIGURES} text-xl font-black leading-none tabular-nums md:text-3xl`}>{unit.value}</span>
            <span className={`${FAINT} mt-1 text-[8px] font-semibold uppercase tracking-[0.22em] md:text-[9px]`}>{unit.label}</span>
          </span>
        </React.Fragment>
      ))}
    </div>
  )
}

/**
 * The map's placeholder: the same footprint as the finished section —
 * title, countdown, two chamber banners, tabs over the real US outline,
 * and market rows — so nothing pops in from nowhere.
 */
const MidtermMapSkeleton = ({ className = '' }) => {
  const i18n = useI18n()

  return (
    <section
      aria-busy="true"
      aria-label={i18n('midterm_map.title')}
      className={`relative isolate mb-2 ${INK} [--midterm-empty-fill:rgba(15,40,52,0.08)] [--midterm-active-stroke:rgba(15,40,52,0.55)] [--midterm-label-fill:#16192f] [--midterm-label-halo:rgba(255,255,255,0.92)] [html[data-theme='dark']_&]:[--midterm-empty-fill:rgba(255,255,255,0.07)] [html[data-theme='dark']_&]:[--midterm-active-stroke:rgba(255,255,255,0.9)] [html[data-theme='dark']_&]:[--midterm-label-fill:#ffffff] [html[data-theme='dark']_&]:[--midterm-label-halo:rgba(3,3,10,0.88)] ${className}`}>
      <MidtermBackdrop />
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-surface-1 via-surface-1/75 to-transparent md:h-44" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-surface-1 to-transparent md:h-24" />

      <div className="relative flex flex-col gap-4 p-3 md:gap-8 md:p-6">
        <div className="flex flex-col items-start gap-3 px-1 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-x-6 md:px-2">
          <h2 className="m-0 text-2xl font-black leading-none tracking-[-0.02em] md:text-4xl">{i18n('midterm_map.title')}</h2>
          <SkeletonCountdown i18n={i18n} />
        </div>

        <div className="grid gap-4 px-1 md:grid-cols-2 md:gap-5 md:px-2">
          <BannerSkeleton />
          <BannerSkeleton />
        </div>

        <div className="mt-1 grid gap-4 pt-4 md:mt-4 md:gap-5 md:pt-10 lg:grid-cols-[minmax(0,1.65fr)_minmax(280px,0.9fr)]">
          <div className="flex flex-col gap-3 p-0 md:gap-5 md:p-1">
            <div className="scrollbar-none -mx-1 flex justify-start gap-2 overflow-x-auto px-1 md:mx-0 md:flex-wrap md:justify-center md:overflow-visible">
              {TABS.map((tab, index) => (
                <span key={tab.id} className={`${TAB_PILL} ${index === 0 ? TAB_ON : TAB_OFF}`}>
                  {i18n(tab.labelKey)}
                  <GlassBlock className="h-2.5 w-5 rounded" />
                </span>
              ))}
            </div>
            <UsStateMap
              byState={EMPTY_BY_STATE}
              activeCode={null}
              onPreview={() => {}}
              onPin={() => {}}
              noMarketLabel={i18n('midterm_map.no_market')}
              revealOnMount={false}
            />
          </div>

          <div className="flex min-h-0 flex-col p-1 pt-2 md:min-h-[280px] md:p-2 md:pt-12">
            <GlassBlock className="h-2.5 w-28 rounded" />
            <GlassBlock className="mt-2 h-7 w-36 rounded md:h-9 md:w-40" />
            <div className="mt-4 flex flex-col gap-2">
              <RaceCardSkeleton />
              <RaceCardSkeleton />
              <RaceCardSkeleton />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default MidtermMapSkeleton
